import createClient, {
  type ClientPathsWithMethod,
  type MaybeOptionalInit,
  type MethodResponse,
  type Middleware,
} from 'openapi-fetch';

import { env } from '../config/env';
import type { paths } from './schema';

/**
 * Хранилище токенов (AD-A3): access — только в памяти, refresh — во вкладке.
 *
 * `sessionStorage`, а не `localStorage`: refresh живёт с вкладкой и переживает перезагрузку,
 * но не закрытие окна и не другую вкладку. Принятый и названный риск — скрипт на странице
 * может его прочитать; для внутреннего инструмента одного пользователя за CSP-allowlist
 * это принято (спайн, AD-A3). Чтение и запись обёрнуты: в приватном окне и под
 * ограничениями хранилище бросает, и сессия тогда просто не переживёт перезагрузку.
 */
const REFRESH_KEY = 'aura-admin.refresh';

let accessToken: string | null = null;
let refreshToken: string | null = readStoredRefresh();

function readStoredRefresh(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeStoredRefresh(value: string | null): void {
  try {
    if (value === null) {
      sessionStorage.removeItem(REFRESH_KEY);
    } else {
      sessionStorage.setItem(REFRESH_KEY, value);
    }
  } catch {
    // Хранилище недоступно — сессия остаётся в памяти до перезагрузки.
  }
}

/**
 * Обмен, который уже идёт. Пустое значение — обмена нет (AD-A3 наследует AD-T19).
 *
 * Контракт `POST /auth/refresh`: «a client that can refresh from more than one place must
 * serialise those calls itself». Два экрана, получившие `401` одновременно, иначе обменяли бы
 * один и тот же токен дважды, и проигравший получил бы `401` на живой сессии.
 */
let refreshInFlight: Promise<boolean> | null = null;

export const tokenStore = {
  set(tokens: { accessToken: string; refreshToken: string }): void {
    accessToken = tokens.accessToken;
    refreshToken = tokens.refreshToken;
    writeStoredRefresh(tokens.refreshToken);
  },
  clear(): void {
    accessToken = null;
    refreshToken = null;
    writeStoredRefresh(null);
    // Полёт принадлежит паре, которой больше нет: оставь его — и вызов, пришедший после
    // логаута, присоединится к обмену закрытой сессии.
    refreshInFlight = null;
  },
  get access(): string | null {
    return accessToken;
  },
  get refresh(): string | null {
    return refreshToken;
  },
};

/**
 * Подписчики на потерю сессии (AD-A3 наследует AD-T8). Подписчик один — гейт в `app/`;
 * набор, а не ссылка, потому что StrictMode монтирует эффект дважды.
 */
const sessionLostListeners = new Set<() => void>();

export function onSessionLost(listener: () => void): () => void {
  sessionLostListeners.add(listener);
  return () => {
    sessionLostListeners.delete(listener);
  };
}

function raiseSessionLost(): void {
  for (const listener of [...sessionLostListeners]) {
    listener();
  }
}

/**
 * Отказ API одним типом: конверт `code`/`message`/`requestId` бэкенда становится `ApiError`
 * здесь, а не у потребителя. `requestId` показывается человеку — его можно сопоставить с
 * логами сервера.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

/** Пройдёт ли повтор там, где отказал первый запрос: временные названы поимённо. */
export function isRetryableApiFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  if (error.code === 'NETWORK_ERROR') {
    return true;
  }
  return error.status === 429 || error.status >= 500;
}

type ErrorEnvelope = {
  code?: string;
  message?: string;
  requestId?: string;
  details?: Record<string, unknown>;
};
type TokenPair = { accessToken: string; refreshToken: string };

/** Статус отказа, у которого ответа не было вовсе: HTTP-статуса у него нет. */
const NO_RESPONSE = 0;

/**
 * Запрос, отложенный на случай повтора после `401`, и токен, с которым он ушёл. Клон
 * снимается **до** отправки: у запроса с прочитанным телом `clone()` бросает.
 */
type PendingRetry = { request: Request; sentWith: string | null };
const pendingRetries = new Map<string, PendingRetry>();

/** Внутренности для тестов, а не публичная поверхность. */
export const __internal = { pendingRetries };

/**
 * Повторять после `401` можно не всё: `/auth/*` живёт на одноразовых credential — refresh
 * одноразовый, `logout` неидемпотентен, а `401` там — отказ самого credential.
 */
function canReplay(schemaPath: string): boolean {
  return !schemaPath.startsWith('/auth/');
}

const sessionMiddleware: Middleware = {
  onRequest({ request, schemaPath, id }) {
    const sent = accessToken ? withBearer(request, accessToken) : request;
    if (refreshToken && canReplay(schemaPath)) {
      pendingRetries.set(id, { request: sent.clone(), sentWith: accessToken });
    }
    return sent;
  },

  async onResponse({ id, response, options }) {
    const pending = pendingRetries.get(id);
    pendingRetries.delete(id);

    if (response.status !== 401 || !pending || !refreshToken) {
      return undefined;
    }

    // Пока запрос был в полёте, сессию мог обновить кто-то другой: этот `401` — про прошлый
    // access-токен, и обменивать свежую пару ещё раз значит ротировать её впустую.
    if (accessToken !== pending.sentWith) {
      return replay(pending.request, options.fetch);
    }
    if (await tryRefresh()) {
      return replay(pending.request, options.fetch);
    }

    // `401`, переживший обновление, — класс «сессия»: о нём знает один гейт, экран получает
    // обычный отказ, который и так не показывает.
    raiseSessionLost();
    return undefined;
  },

  onError({ id, error }) {
    pendingRetries.delete(id);
    return transportError(error);
  },
};

const client = createClient<paths>({
  baseUrl: env.apiBaseUrl,
  // `createClient` запоминает `globalThis.fetch` в момент создания, а тесты подменяют
  // глобальный `fetch` позже — без этой обёртки они стабили бы уже не тот `fetch`.
  fetch: (request) => fetch(request),
});
client.use(sessionMiddleware);

type GetPath = ClientPathsWithMethod<typeof client, 'get'>;
type PostPath = ClientPathsWithMethod<typeof client, 'post'>;
type DeletePath = ClientPathsWithMethod<typeof client, 'delete'>;

/** `init` обязателен там, где контракт требует тело или параметры, и не нужен иначе. */
type InitArg<Init> = undefined extends Init ? [init?: Init] : [init: Init];

/** Результат openapi-fetch: успех несёт `data`, отказ — разобранный конверт ошибки. */
type ApiResult = { data?: unknown; error?: unknown; response: Response };

function transportError(error: unknown): ApiError {
  return new ApiError(
    NO_RESPONSE,
    'NETWORK_ERROR',
    error instanceof Error ? error.message : 'Сеть недоступна',
  );
}

async function unwrap(outcome: Promise<ApiResult>): Promise<unknown> {
  let result: ApiResult;
  try {
    result = await outcome;
  } catch (error) {
    throw error instanceof ApiError ? error : transportError(error);
  }
  if (result.error !== undefined) {
    const envelope = (result.error ?? {}) as ErrorEnvelope;
    throw new ApiError(
      result.response.status,
      envelope.code ?? 'UNKNOWN',
      envelope.message ?? `Ответ ${result.response.status} без конверта ошибки`,
      envelope.requestId,
      envelope.details,
    );
  }
  return result.data;
}

/**
 * Единственный вход в API (AD-A2). Путь проверяется контрактом, тип ответа выводится из пути;
 * слота для своего типа ответа в подписи нет. Ошибки уходят одним `ApiError`.
 */
export const api = {
  get<Path extends GetPath>(
    path: Path,
    ...init: InitArg<MaybeOptionalInit<paths[Path], 'get'>>
  ): Promise<MethodResponse<typeof client, 'get', Path>> {
    return unwrap(send(client.GET, path, init[0])) as Promise<
      MethodResponse<typeof client, 'get', Path>
    >;
  },
  post<Path extends PostPath>(
    path: Path,
    ...init: InitArg<MaybeOptionalInit<paths[Path], 'post'>>
  ): Promise<MethodResponse<typeof client, 'post', Path>> {
    return unwrap(send(client.POST, path, init[0])) as Promise<
      MethodResponse<typeof client, 'post', Path>
    >;
  },
  delete<Path extends DeletePath>(
    path: Path,
    ...init: InitArg<MaybeOptionalInit<paths[Path], 'delete'>>
  ): Promise<MethodResponse<typeof client, 'delete', Path>> {
    return unwrap(send(client.DELETE, path, init[0])) as Promise<
      MethodResponse<typeof client, 'delete', Path>
    >;
  },
};

/** Сужение техническое: перегрузку клиента компилятор не сводит с обобщённым `Path`. */
function send(
  method: typeof client.GET | typeof client.POST | typeof client.DELETE,
  path: string,
  init: unknown,
): Promise<ApiResult> {
  const call = method as (path: string, init?: unknown) => Promise<ApiResult>;
  return call(path, init);
}

function withBearer(request: Request, token: string): Request {
  const headers = new Headers(request.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return new Request(request, { headers });
}

/** Повтор отложенным клоном с тем токеном, что лежит в хранилище сейчас. */
function replay(pending: Request, transport: typeof globalThis.fetch): Promise<Response> {
  return transport(accessToken ? withBearer(pending, accessToken) : pending);
}

/** Обновляет пару, не запуская второй обмен поверх первого. */
export function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  const attempted = refreshToken;
  if (!attempted) {
    return Promise.resolve(false);
  }
  const flight = exchangeRefreshToken(attempted).finally(() => {
    refreshInFlight = null;
  });
  refreshInFlight = flight;
  return flight;
}

/** Сам обмен. Вызывается только из `tryRefresh()` — ровно один на полёт. */
async function exchangeRefreshToken(attempted: string): Promise<boolean> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: attempted }),
    });
  } catch {
    // Обрыв сети: токен не израсходован, обмен можно повторить позже.
    return false;
  }

  if (response.ok) {
    const tokens = await readTokenPair(response);
    if (!tokens) {
      return false;
    }
    if (refreshToken === attempted) {
      tokenStore.set(tokens);
      return true;
    }
    return storesNewerPair(attempted);
  }

  if (response.status === 401) {
    if (storesNewerPair(attempted)) {
      return true;
    }
    if (refreshToken === attempted) {
      tokenStore.clear();
    }
    return false;
  }

  // `429` отбит лимитером, `5xx` откатился — токен ещё можно пробовать.
  if (response.status === 429 || response.status >= 500) {
    return false;
  }

  tokenStore.clear();
  return false;
}

function storesNewerPair(attempted: string): boolean {
  return refreshToken !== null && refreshToken !== attempted;
}

/** Пара из успешного ответа или `null`: `200` за прокси приходит и с чужим телом. */
async function readTokenPair(response: Response): Promise<TokenPair | null> {
  const body: unknown = await response.json().catch(() => null);
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const { accessToken: access, refreshToken: refresh } = body as Record<string, unknown>;
  return typeof access === 'string' &&
    access !== '' &&
    typeof refresh === 'string' &&
    refresh !== ''
    ? { accessToken: access, refreshToken: refresh }
    : null;
}
